import { Bell, Gauge, Layers, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { motion } from "framer-motion";
import type { Orizonte12Pulse } from "@/lib/orizonte-12";

const pillarIcons = [Gauge, Layers, Bell, ShieldCheck, Zap, Sparkles];

export const Orizonte12Banner = ({ pulse }: { pulse: Orizonte12Pulse }) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-t-2xl border-0 bg-[#010604] px-5 py-8"
      aria-labelledby="orizonte-12-title"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(204,255,0,0.15),transparent_60%)]" />

      <div className="relative z-10 mx-auto max-w-3xl">
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-3 flex h-14 w-14 items-center justify-center">
            <div className="absolute inset-1 rounded-full border-8 border-orizonte-lime/70 shadow-[0_0_20px_rgba(204,255,0,0.3)]" />
            <div className="absolute h-px w-20 bg-gradient-to-r from-transparent via-orizonte-lime to-transparent shadow-[0_0_10px_rgba(204,255,0,0.8)]" />
            <div className="absolute h-1.5 w-1.5 rounded-full bg-white shadow-[0_0_12px_4px_rgba(204,255,0,0.6)]" />
          </div>

          <p className="mb-2 text-[0.6rem] font-semibold uppercase tracking-[0.6em] text-white/70">
            Orizonte
          </p>
          <h2
            id="orizonte-12-title"
            className="text-2xl font-light uppercase tracking-[0.08em] text-white sm:text-3xl"
          >
            <span className="font-semibold text-orizonte-lime">1.2</span>{" "}
            <span>Update</span>
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-orizonte-textMuted">
            {pulse.headline}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-6">
          {pulse.launchPillars.map((pillar, index) => {
            const Icon = pillarIcons[index] || Sparkles;
            return (
              <motion.div
                key={pillar.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * index, duration: 0.35, ease: "easeOut" }}
                className="group rounded-xl border border-orizonte-lime/15 bg-black/20 p-3 text-center transition-colors hover:border-orizonte-lime/30 hover:bg-orizonte-lime/[0.03]"
              >
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-lg text-orizonte-lime transition-transform group-hover:-translate-y-0.5">
                  <Icon className="h-8 w-8 stroke-[1.5]" />
                </div>
                <h3 className="text-[0.65rem] font-medium uppercase leading-relaxed tracking-[0.12em] text-white">
                  {pillar.title}
                </h3>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-center gap-4 text-[0.55rem] font-semibold uppercase tracking-[0.5em] text-orizonte-lime">
          <span className="h-px w-16 bg-gradient-to-r from-transparent to-orizonte-lime/60" />
          <span>Orizonte 1.2</span>
          <span className="h-px w-16 bg-gradient-to-l from-transparent to-orizonte-lime/60" />
        </div>
      </div>
    </motion.section>
  );
};
